<template>
  <v-container fluid>
    <v-layout align-start>
      <v-flex>
        <v-stepper flat non-linear v-model="page" class="transparent-stepper">
          <v-stepper-content :step="1" class="pa-0">
            <div class="page-header mb-6">
              <div class="d-flex align-center">
                <v-icon large color="primary" class="mr-3">corporate_fare</v-icon>
                <div>
                  <h1 class="text-h4 font-weight-bold grey--text text--darken-3 mb-0">Perfiles de Usuarios</h1>
                  <span class="text-subtitle-1 grey--text">Administra los roles y permisos de acceso al sistema</span>
                </div>
              </div>
            </div>

            <v-card class="modern-card" elevation="2">
              <v-data-table :headers="headers" :items="profiles_computed" :search="search" class="modern-table"
                fixed-header height="60vh" item-key="id" single-expand :expanded.sync="expandedProfiles">
                <template v-slot:top>
                  <v-toolbar flat color="transparent" class="px-2">
                    <v-badge color="primary" :content="profiles_computed.length" overlap class="mr-4">
                      <span class="text-h6 font-weight-medium">Perfiles</span>
                    </v-badge>

                    <v-spacer></v-spacer>

                    <v-text-field v-model="search" prepend-inner-icon="search" label="Buscar perfil..." outlined dense
                      hide-details class="search-field mr-4" rounded></v-text-field>

                    <v-autocomplete v-model="module_filter" :items="system_modules" item-value="id"
                      item-text="description" label="Filtrar por Módulo" outlined dense hide-details
                      class="module-filter mr-4" rounded></v-autocomplete>

                    <v-switch v-model="excludeInactive" label="Excluir inactivos" hide-details dense
                      color="primary" class="mt-0 mr-4"></v-switch>

                    <v-btn color="secondary" outlined rounded class="px-6 font-weight-bold mr-2"
                      @click="openSystemConfig()">
                      <v-icon left>settings</v-icon>
                      Estructura del Sistema
                    </v-btn>

                    <v-btn color="primary" depressed rounded class="px-6 font-weight-bold" @click="add_profile()">
                      <v-icon left>add</v-icon>
                      Nuevo Perfil
                    </v-btn>
                  </v-toolbar>
                </template>

                <template v-slot:[`item.code`]="{ item }">
                  <v-chip small label color="primary" outlined class="font-weight-bold px-3">
                    {{ item.code }}
                  </v-chip>
                </template>

                <template v-slot:[`item.module_description`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="24" color="grey lighten-4" class="mr-2">
                      <v-icon x-small color="grey darken-1">apps</v-icon>
                    </v-avatar>
                    <span class="font-weight-medium grey--text text--darken-2">
                      {{ item.module_description }}
                    </span>
                  </div>
                </template>

                <template v-slot:[`item.user_count`]="{ item, isExpanded }">
                  <v-btn small text color="primary" class="text-none" :disabled="!profileCount(item).total"
                    @click="toggleUsers(item)" title="Ver usuarios de este perfil">
                    <v-icon left small>group</v-icon>
                    {{ profileCount(item).total }}
                    <v-chip v-if="profileCount(item).custom" x-small color="orange lighten-4" text-color="orange darken-3"
                      class="ml-1">{{ profileCount(item).custom }} custom</v-chip>
                    <v-icon right small v-if="profileCount(item).total">{{ isExpanded ? 'expand_less' : 'expand_more' }}</v-icon>
                  </v-btn>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <v-btn icon color="primary" @click="edit_profile(item)" class="mx-1" title="Editar Perfil">
                    <v-icon>edit</v-icon>
                  </v-btn>
                </template>

                <!-- Fila expandible: usuarios del perfil (sin modal) -->
                <template v-slot:expanded-item="{ headers }">
                  <td :colspan="headers.length" class="pa-0 grey lighten-5">
                    <div class="px-4 py-2">
                      <div class="text-caption grey--text text--darken-1 mb-1">
                        Mostrando: {{ visibleProfileUsers.length }} · Custom:
                        {{ visibleProfileUsers.filter(u => u.is_custom).length }}
                        <span v-if="excludeInactive">· (inactivos ocultos)</span>
                        — los <strong>custom</strong> no se actualizan con el toggle; edítalos manualmente.
                      </div>
                      <v-progress-linear v-if="loadingUsers" indeterminate color="primary"></v-progress-linear>
                      <v-row dense>
                        <v-col v-for="u in visibleProfileUsers" :key="u.use_id" cols="12" sm="6" md="4">
                          <v-card outlined class="pa-2 d-flex align-center" style="gap:8px;">
                            <v-avatar size="32" color="primary lighten-4">
                              <span class="primary--text text-caption font-weight-bold">
                                {{ (u.use_fullname || '?').substring(0,1) }}</span>
                            </v-avatar>
                            <div class="flex-grow-1" style="min-width:0;">
                              <div class="text-body-2 font-weight-medium text-truncate">
                                {{ u.use_fullname }}
                                <v-chip v-if="u.is_custom" x-small color="orange lighten-4" text-color="orange darken-3">CUSTOM</v-chip>
                                <v-chip v-if="!u.active" x-small color="red lighten-4" text-color="red darken-2">Inactivo</v-chip>
                              </div>
                              <div class="text-caption grey--text text-truncate">
                                @{{ u.use_username }} · Cód: {{ u.employee_code || 'N/A' }}
                              </div>
                            </div>
                            <v-btn icon small color="primary" @click="editUserFromProfile(u)" title="Editar usuario">
                              <v-icon small>edit</v-icon>
                            </v-btn>
                          </v-card>
                        </v-col>
                        <v-col v-if="!loadingUsers && visibleProfileUsers.length === 0" cols="12">
                          <div class="text-center grey--text py-3">Sin usuarios asignados.</div>
                        </v-col>
                      </v-row>
                    </div>
                  </td>
                </template>
              </v-data-table>
            </v-card>
          </v-stepper-content>

          <v-stepper-content :step="2" class="pa-0">
            <v-container fluid class="pa-4">
              <v-row>
                <v-col cols="12" md="4">
                  <v-card class="modern-card sticky-card" elevation="2">
                    <v-card-title class="d-flex align-center pb-2">
                      <v-icon color="primary" class="mr-2">assignment_ind</v-icon>
                      Detalles del Perfil
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="pt-6">
                      <v-autocomplete v-model="new_profile.module_id" :items="system_modules" item-value="id"
                        item-text="description" label="Módulo" outlined dense class="mb-4" @change="clear_features()"
                        rounded></v-autocomplete>

                      <v-text-field v-model="new_profile.description" label="Descripción" outlined dense class="mb-4"
                        rounded></v-text-field>

                      <v-text-field v-model="new_profile.code" label="Código" outlined dense class="mb-4"
                        rounded></v-text-field>

                      <v-switch v-model="new_profile.set_update" label="Actualizar usuarios vinculados" inset
                        color="primary" @change="info_set_update()" :disabled="new_profile.profile_id == null"
                        class="mb-6"></v-switch>

                      <div class="d-flex justify-space-between pt-4">
                        <v-btn outlined color="error" @click="clear()" :disabled="loading" rounded class="px-6">
                          <v-icon left>arrow_back</v-icon>
                          Cancelar
                        </v-btn>
                        <v-btn color="primary" @click="save_system_profile()" :loading="loading" rounded class="px-6"
                          depressed>
                          <v-icon left>save</v-icon>
                          {{ new_profile.profile_id ? "Actualizar" : "Guardar" }}
                        </v-btn>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" md="8">
                  <v-card class="modern-card" elevation="2">
                    <v-card-title class="d-flex align-center pb-2">
                      <v-icon color="primary" class="mr-2">security</v-icon>
                      Permisos y Accesos
                      <v-spacer></v-spacer>
                      <v-btn x-small text color="primary" class="mr-2" @click="toggleAllGroups">
                        <v-icon left x-small>{{ allGroupsCollapsed ? "mdi-unfold-more-horizontal" : "mdi-unfold-less-horizontal" }}</v-icon>
                        {{ allGroupsCollapsed ? "Expandir todo" : "Colapsar todo" }}
                      </v-btn>
                      <v-text-field v-model="feature_search" prepend-inner-icon="search" label="Buscar permiso..."
                        outlined dense hide-details rounded class="search-field-small"></v-text-field>
                    </v-card-title>
                    <v-divider></v-divider>

                    <v-data-table ref="permTable" :headers="headers_feature" :items="features" :search="feature_search"
                      item-key="feature_id" sort-by="view_description" group-by="view_description"
                      class="modern-table feature-table" fixed-header hide-default-footer :items-per-page="-1"
                      height="65vh">
                      <template v-slot:group.header="{ group, headers, toggle, isOpen }">
                        <td :colspan="headers.length" class="group-header-row" @click="toggle" style="cursor: pointer">
                          <v-btn icon small>
                            <v-icon>{{ isOpen ? 'expand_more' : 'chevron_right' }}</v-icon>
                          </v-btn>
                          <v-icon small color="primary" class="mr-2">window</v-icon>
                          <span class="font-weight-bold grey--text text--darken-2">Pantalla: {{ group }}</span>
                        </td>
                      </template>

                      <template v-slot:[`item.description`]="{ item }">
                        <div class="d-flex flex-column py-1">
                          <span class="grey--text text--darken-4 font-weight-medium">{{ item.description }}</span>
                          <span class="grey--text text--lighten-1 caption text-uppercase"
                            style="font-size: 0.65rem !important; letter-spacing: 0.05em">
                            Cód: {{ item.feature_code }}
                          </span>
                        </div>
                      </template>

                      <template v-slot:[`item.access_type`]="{ item }">
                        <v-chip small label :color="getAccessTypeColor(item.access_type)"
                          class="white--text font-weight-bold" v-if="item.access_type" elevation="1">
                          <v-icon x-small left color="white">
                            {{ getAccessTypeIcon(item.access_type) }}
                          </v-icon>
                          {{ item.access_type }}
                        </v-chip>
                      </template>

                      <template v-slot:[`item.include`]="{ item }">
                        <v-checkbox dense hide-details v-model="item.include" color="primary"
                          :disabled="new_profile.module_id == null"></v-checkbox>
                      </template>
                    </v-data-table>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-stepper-content>
        </v-stepper>
      </v-flex>
    </v-layout>

    <!-- Dialogo de Configuracion del Sistema (Premium Redesign) -->
    <v-dialog v-model="systemConfigDialog" fullscreen transition="dialog-bottom-transition" persistent>
      <v-card class="modern-card overflow-hidden" height="100vh" style="display: flex; flex-direction: column;">
        <v-toolbar flat color="primary" dark class="flex-grow-0">
          <v-icon color="white" class="mr-3">settings_suggest</v-icon>
          <v-toolbar-title class="font-weight-bold">Configuración de Estructura del Sistema</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="systemConfigDialog = false"><v-icon>close</v-icon></v-btn>
        </v-toolbar>

        <div class="d-flex flex-grow-1 overflow-hidden" style="background: #f8fafc;">
          <!-- Sidebar Navigation -->
          <div class="sidebar-nav pa-4 elevation-1"
            style="width: 260px; background: white; border-right: 1px solid #e2e8f0;">
            <div class="text-overline grey--text mb-4">Administración</div>
            <v-list nav dense class="pa-0">
              <v-list-item-group v-model="systemConfigTab" color="primary" mandatory>
                <v-list-item :value="0" class="rounded-lg mb-2 py-2">
                  <v-list-item-icon><v-icon>apps</v-icon></v-list-item-icon>
                  <v-list-item-content><v-list-item-title
                      class="font-weight-bold">Módulos</v-list-item-title></v-list-item-content>
                </v-list-item>
                <v-list-item :value="1" class="rounded-lg mb-2 py-2">
                  <v-list-item-icon><v-icon>window</v-icon></v-list-item-icon>
                  <v-list-item-content><v-list-item-title
                      class="font-weight-bold">Pantallas</v-list-item-title></v-list-item-content>
                </v-list-item>
                <v-list-item :value="2" class="rounded-lg mb-2 py-2">
                  <v-list-item-icon><v-icon>security</v-icon></v-list-item-icon>
                  <v-list-item-content><v-list-item-title
                      class="font-weight-bold">Accesos</v-list-item-title></v-list-item-content>
                </v-list-item>
                <v-divider class="my-4"></v-divider>
                <v-list-item :value="3" class="rounded-lg mb-2 py-2">
                  <v-list-item-icon><v-icon>account_tree</v-icon></v-list-item-icon>
                  <v-list-item-content><v-list-item-title class="font-weight-bold">Mapa
                      Visual</v-list-item-title></v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </div>

          <!-- Main Content Area -->
          <div class="flex-grow-1 overflow-y-auto pa-6">
            <v-tabs-items v-model="systemConfigTab" style="background: transparent;">
              <!-- MODULOS -->
              <v-tab-item :value="0">
                <v-row>
                  <v-col cols="12" md="4">
                    <v-card class="modern-card pa-4" elevation="1">
                      <div class="text-h6 font-weight-bold primary--text mb-4">Crear Módulo</div>
                      <v-text-field v-model="new_module.code" label="Código" outlined dense rounded
                        counter="10"></v-text-field>
                      <v-text-field v-model="new_module.description" label="Nombre/Descripción" outlined dense
                        rounded></v-text-field>
                      <v-btn color="primary" block rounded depressed @click="saveModule()" :loading="loading"
                        class="mt-2">
                        <v-icon left>add_circle</v-icon> Registrar
                      </v-btn>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="8">
                    <v-card class="modern-card" elevation="1">
                      <v-toolbar flat color="transparent">
                        <v-text-field v-model="dialogSearch" prepend-inner-icon="search" placeholder="Buscar módulos..."
                          outlined dense hide-details rounded class="search-field-dialog"></v-text-field>
                      </v-toolbar>
                      <v-divider></v-divider>
                      <v-data-table
                        :headers="[{ text: 'CÓDIGO', value: 'code' }, { text: 'NOMBRE', value: 'description' }]"
                        :items="system_modules" :search="dialogSearch" dense fixed-header height="45vh"
                        class="modern-table"></v-data-table>
                    </v-card>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- PANTALLAS -->
              <v-tab-item :value="1">
                <v-row>
                  <v-col cols="12" md="4">
                    <v-card class="modern-card pa-4" elevation="1">
                      <div class="text-h6 font-weight-bold primary--text mb-4">Añadir Pantalla</div>
                      <v-select v-model="new_view.module_id" :items="system_modules" item-value="id"
                        item-text="description" label="Seleccione Módulo" outlined dense rounded></v-select>
                      <v-text-field v-model="new_view.code" label="Código de Vista" outlined dense rounded
                        placeholder="INV-01"></v-text-field>
                      <v-text-field v-model="new_view.description" label="Nombre de Pantalla" outlined dense
                        rounded></v-text-field>
                      <v-btn color="primary" block rounded depressed @click="saveView()" :loading="loading"
                        class="mt-2">
                        <v-icon left>save</v-icon> Crear Pantalla
                      </v-btn>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="8">
                    <v-card class="modern-card" elevation="1">
                      <v-toolbar flat color="transparent">
                        <v-text-field v-model="dialogSearch" prepend-inner-icon="search"
                          placeholder="Filtrar por nombre o módulo..." outlined dense hide-details rounded
                          class="search-field-dialog"></v-text-field>
                      </v-toolbar>
                      <v-divider></v-divider>
                      <v-data-table
                        :headers="[{ text: 'MÓDULO', value: 'module_description' }, { text: 'CÓD.', value: 'code' }, { text: 'PANTALLA', value: 'description' }]"
                        :items="system_views_config_computed" :search="dialogSearch" dense fixed-header height="45vh"
                        class="modern-table"></v-data-table>
                    </v-card>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- ACCESOS -->
              <v-tab-item :value="2">
                <v-row>
                  <v-col cols="12" md="2">
                    <v-card class="modern-card pa-4" elevation="1">
                      <div class="text-subtitle-1 font-weight-bold primary--text mb-4">Definir Acceso</div>
                      <v-autocomplete v-model="new_feature.view_id" :items="system_views_all" item-value="id"
                        item-text="description" label="Pantalla/Vista" outlined dense rounded></v-autocomplete>
                      <v-select v-model="new_feature.access_type_id" :items="access_types" item-value="id"
                        item-text="description" label="Tipo de Acción" outlined dense rounded></v-select>
                      <v-text-field v-model="new_feature.code" label="Cód. Permiso" outlined dense rounded
                        placeholder="BOTON_GUARDAR"></v-text-field>
                      <v-text-field v-model="new_feature.description" label="Etiqueta" outlined dense rounded
                        placeholder="Guardar Cambios"></v-text-field>
                      <v-btn color="primary" block rounded depressed @click="saveFeature()" :loading="loading"
                        class="mt-2">
                        <v-icon left>verified_user</v-icon> Registrar Acceso
                      </v-btn>
                    </v-card>
                  </v-col>
                  <!-- Lista de accesos (seleccionable) -->
                  <v-col cols="12" md="4">
                    <v-card class="modern-card" elevation="1">
                      <v-toolbar flat color="transparent" dense>
                        <v-text-field v-model="dialogSearch" prepend-inner-icon="search" placeholder="Buscar accesos..."
                          outlined dense hide-details rounded class="search-field-dialog"></v-text-field>
                      </v-toolbar>
                      <v-divider></v-divider>
                      <v-data-table :headers="headers_feature_config" :items="system_features_config_computed"
                        :search="dialogSearch" dense fixed-header height="60vh" class="modern-table clickable-rows"
                        item-key="feature_id" :item-class="featureRowClass" @click:row="selectFeatureRow">
                        <template v-slot:[`item.access_type`]="{ item }">
                          <v-chip x-small label dark :color="getAccessTypeColor(item.access_type)"
                            class="font-weight-bold">{{ item.access_type }}</v-chip>
                        </template>
                      </v-data-table>
                    </v-card>
                  </v-col>
                  <!-- Usuarios del acceso seleccionado -->
                  <v-col cols="12" md="6">
                    <v-card class="modern-card" elevation="1">
                      <v-toolbar flat color="transparent" dense>
                        <v-icon color="primary" class="mr-2">verified_user</v-icon>
                        <span class="font-weight-bold text-truncate">
                          {{ selectedFeature ? selectedFeature.description : 'Selecciona un acceso' }}
                        </span>
                        <v-spacer></v-spacer>
                        <v-switch v-if="selectedFeature && featurePanelTab === 0" v-model="excludeInactiveFeatureUsers"
                          dense hide-details class="mt-0 pt-0" color="primary" label="Excluir inactivos"></v-switch>
                      </v-toolbar>
                      <v-tabs v-if="selectedFeature" v-model="featurePanelTab" grow>
                        <v-tab>
                          <v-icon left small>people</v-icon>Usuarios ({{ visibleFeatureUsers.length }})
                        </v-tab>
                        <v-tab>
                          <v-icon left small>badge</v-icon>Perfiles ({{ featureProfiles.length }})
                        </v-tab>
                      </v-tabs>
                      <v-divider></v-divider>
                      <v-tabs-items v-if="selectedFeature" v-model="featurePanelTab">
                        <!-- USUARIOS -->
                        <v-tab-item>
                          <div class="px-3 pt-3">
                            <v-text-field v-model="featureUserSearch" prepend-inner-icon="search"
                              placeholder="Buscar usuario por nombre, código, puesto..." outlined dense hide-details
                              rounded></v-text-field>
                          </div>
                          <v-data-table :headers="headers_feature_users" :items="visibleFeatureUsers"
                            :search="featureUserSearch" :loading="loadingFeatureUsers" dense fixed-header height="48vh"
                            class="modern-table clickable-rows mt-2" :items-per-page="25"
                            :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
                            @click:row="editFeatureUser">
                            <template v-slot:[`item.use_fullname`]="{ item }">
                              <div class="d-flex flex-column py-1">
                                <span class="font-weight-medium">{{ item.use_fullname }}</span>
                                <span class="text-caption grey--text">@{{ item.use_username }}</span>
                              </div>
                            </template>
                            <template v-slot:[`item.active`]="{ item }">
                              <v-chip x-small label :color="item.active ? 'success' : 'grey lighten-1'" dark>
                                {{ item.active ? 'Activo' : 'Inactivo' }}
                              </v-chip>
                            </template>
                          </v-data-table>
                        </v-tab-item>
                        <!-- PERFILES -->
                        <v-tab-item>
                          <v-data-table :headers="headers_feature_profiles" :items="featureProfiles" dense fixed-header
                            height="56vh" class="modern-table clickable-rows" :items-per-page="-1" hide-default-footer
                            @click:row="editProfileFromFeature" no-data-text="Ningún perfil incluye este acceso.">
                            <template v-slot:[`item.code`]="{ item }">
                              <v-chip x-small label color="primary" outlined class="font-weight-bold">{{ item.code }}</v-chip>
                            </template>
                          </v-data-table>
                        </v-tab-item>
                      </v-tabs-items>
                      <div v-else class="pa-8 text-center grey--text">
                        <v-icon size="40" color="grey lighten-1">touch_app</v-icon>
                        <div class="mt-2">Haz clic en un acceso de la lista para ver sus usuarios y perfiles.</div>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- MAPA VISUAL (PREMIUM TREE) -->
              <v-tab-item :value="3">
                <div class="text-h5 font-weight-bold mb-6 grey--text text--darken-3">Mapa Jerárquico del Sistema</div>
                <div v-if="loading" class="text-center pa-10"><v-progress-circular indeterminate
                    color="primary"></v-progress-circular></div>
                <div v-else>
                  <v-row>
                    <v-col v-for="mod in system_modules" :key="mod.id" cols="12" md="6" lg="4">
                      <v-card class="modern-card pa-0 overflow-hidden mb-4" elevation="1">
                        <div class="pa-3 primary white--text font-weight-bold d-flex align-center">
                          <v-icon color="white" class="mr-2">apps</v-icon>
                          {{ mod.description }}
                          <v-spacer></v-spacer>
                          <v-chip x-small color="rgba(255,255,255,0.2)" dark>{{ mod.code }}</v-chip>
                        </div>
                        <div class="pa-2 grey lighten-5">
                          <div v-for="view in system_views_all.filter(v => v.module_id == mod.id)" :key="view.id"
                            class="mb-2">
                            <div class="d-flex align-center font-weight-medium grey--text text--darken-3 px-2 py-1">
                              <v-icon x-small class="mr-1">chevron_right</v-icon>
                              {{ view.description }}
                            </div>
                            <div class="pl-6 d-flex flex-wrap">
                              <v-chip v-for="feat in system_features.filter(f => f.view_id == view.id)"
                                :key="feat.feature_id" x-small outlined class="mr-1 mb-1"
                                :color="getAccessTypeColor(feat.access_type)">
                                {{ feat.description }}
                              </v-chip>
                              <div v-if="system_features.filter(f => f.view_id == view.id).length == 0"
                                class="caption grey--text pl-2 italic">Sin accesos definidos</div>
                            </div>
                          </div>
                          <div v-if="system_views_all.filter(v => v.module_id == mod.id).length == 0"
                            class="text-center pa-4 caption grey--text">No hay pantallas registradas</div>
                        </div>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: "UserProfiles",
  data: () => ({
    search: null,
    loading: false,
    page: 1,
    module_id: 2,
    module_filter: 5,
    feature_search: null,
    user_profiles: [],
    system_modules: [],
    system_features: [],
    system_views_all: [],
    access_types: [],
    systemConfigDialog: false,
    systemConfigTab: 0,
    dialogSearch: null,
    expandedProfiles: [],
    selectedProfile: null,
    profileUsers: [],
    loadingUsers: false,
    excludeInactive: false,
    allGroupsCollapsed: false,
    selectedFeature: null,
    featureUsers: [],
    featureUserSearch: "",
    loadingFeatureUsers: false,
    excludeInactiveFeatureUsers: false,
    featurePanelTab: 0,
    headers: [
      { text: "Codigo", align: "start", value: "code" },
      { text: "Modulo", value: "module_description" },
      { text: "Perfil", value: "description" },
      { text: "Usuarios", value: "user_count", align: "center", sortable: true },
      { text: "Actions", value: "actions", sortable: false, align: "end" },
    ],
    headers_feature: [
      { text: "ACCESO", align: "start", value: "description", groupable: false },
      { text: "TIPO", value: "access_type", align: "center" },
      { text: "INCLUIR", value: "include", align: "end", width: "2%" },
    ],
    headers_feature_config: [
      { text: "Vista", value: "view_description" },
      { text: "Código", value: "feature_code" },
      { text: "Descripción", value: "description" },
      { text: "Tipo", value: "access_type", align: "center" }
    ],
    headers_feature_users: [
      { text: "Código", value: "employee_code" },
      { text: "Usuario", value: "use_fullname" },
      { text: "Puesto", value: "jobtitle_name" },
      { text: "Departamento", value: "department_name" },
      { text: "Estado", value: "active", align: "center" },
    ],
    headers_feature_profiles: [
      { text: "Módulo", value: "module_description" },
      { text: "Código", value: "code" },
      { text: "Perfil", value: "description" },
    ],
    new_profile: {
      module_id: null,
      profile_id: null,
      code: null,
      description: null,
      set_update: false,
    },
    new_module: { code: null, description: null },
    new_view: { module_id: null, code: null, description: null },
    new_feature: { view_id: null, access_type_id: null, code: null, description: null }
  }),
  computed: {
    features() {
      if (this.new_profile.module_id == null) return this.system_features;
      return this.system_features.filter(
        (element) => element.module_id == this.new_profile.module_id || element.module_id == 0
      );
    },
    profiles_computed() {
      return this.user_profiles.filter((element) => element.module_id == this.module_filter);
    },
    visibleProfileUsers() {
      return this.excludeInactive
        ? this.profileUsers.filter((u) => u.active)
        : this.profileUsers;
    },
    visibleFeatureUsers() {
      return this.excludeInactiveFeatureUsers
        ? this.featureUsers.filter((u) => u.active)
        : this.featureUsers;
    },
    featureProfiles() {
      // Perfiles que incluyen el acceso seleccionado (se calcula del arreglo roles ya cargado)
      if (!this.selectedFeature) return [];
      return this.user_profiles.filter((p) =>
        (p.roles || []).includes(this.selectedFeature.feature_id)
      );
    },
    system_views_config_computed() {
      if (!this.new_view.module_id) return this.system_views_all;
      return this.system_views_all.filter(v => v.module_id == this.new_view.module_id);
    },
    system_features_config_computed() {
      if (!this.new_feature.view_id) return this.system_features;
      return this.system_features.filter(f => f.view_id == this.new_feature.view_id);
    },
  },
  mounted() {
    this.initialize();
  },
  watch: {
    "new_profile.module_id"() {
      // Al cambiar de módulo, las pantallas se muestran expandidas por defecto
      this.allGroupsCollapsed = false;
    },
  },
  methods: {
    profileCount(item) {
      return this.excludeInactive
        ? { total: item.user_count_active || 0, custom: item.custom_count_active || 0 }
        : { total: item.user_count || 0, custom: item.custom_count || 0 };
    },
    async toggleUsers(profile) {
      // Si ya está expandido este perfil, colapsar
      if (this.expandedProfiles.length && this.expandedProfiles[0].id === profile.id) {
        this.expandedProfiles = [];
        return;
      }
      this.selectedProfile = profile;
      this.profileUsers = [];
      this.expandedProfiles = [profile]; // single-expand: abre solo este
      this.loadingUsers = true;
      try {
        const res = await this.api_Get("auth/users_by_profile/" + profile.id);
        this.profileUsers = await res.json();
      } catch (e) {
        this.$toast.error("No se pudieron cargar los usuarios del perfil");
      } finally {
        this.loadingUsers = false;
      }
    },
    editUserFromProfile(user) {
      this.$router.push({ name: "User", query: { user_id: user.use_id } });
    },
    editFeatureUser(user) {
      // Desde la tabla de usuarios de un acceso, ir a editar ese usuario
      this.systemConfigDialog = false;
      this.$router.push({ name: "User", query: { user_id: user.use_id } });
    },
    editProfileFromFeature(profile) {
      // Desde la tabla de perfiles de un acceso, abrir ese perfil para editarlo
      this.systemConfigDialog = false;
      this.edit_profile(profile);
    },
    async initialize() {
      try {
        let req_profiles = await this.api_Get("auth/user_profiles");
        this.user_profiles = await req_profiles.json();
        const re_system_features = await this.api_Get("auth/system_features");
        this.system_features = await re_system_features.json();
        const re_system_modules = await this.api_Get("auth/system_modules");
        this.system_modules = await re_system_modules.json();
      } catch (e) {
        this.$toast.error("Error al cargar datos iniciales");
      }
    },
    async openSystemConfig() {
      this.systemConfigDialog = true;
      this.selectedFeature = null;
      this.featureUsers = [];
      this.featureUserSearch = "";
      this.loading = true;
      try {
        const types = await this.api_Get("auth/system_access_types_list");
        this.access_types = await types.json();
        const views = await this.api_Get("auth/system_views_list");
        this.system_views_all = await views.json();
      } catch (e) {
        this.$toast.error("Error al cargar configuración del sistema");
      } finally {
        this.loading = false;
      }
    },
    async saveModule() {
      if (!this.new_module.code || !this.new_module.description) {
        return this.warningNotification("Indique el código y descripción");
      }
      this.loading = true;
      try {
        const res = await this.api_Post("auth/save_system_module_new", this.new_module);
        if (res.ok) {
          this.$toast.success("Módulo guardado");
          this.new_module = { code: null, description: null };
          await this.initialize();
        }
      } catch (e) {
        this.$toast.error("Error al guardar módulo");
      } finally {
        this.loading = false;
      }
    },
    async saveView() {
      if (!this.new_view.module_id || !this.new_view.code || !this.new_view.description) {
        return this.warningNotification("Todos los campos son obligatorios");
      }
      this.loading = true;
      try {
        const res = await this.api_Post("auth/save_system_view", this.new_view);
        if (res.ok) {
          this.$toast.success("Pantalla guardada");
          this.new_view = { module_id: null, code: null, description: null };
          const views = await this.api_Get("auth/system_views_list");
          this.system_views_all = await views.json();
        }
      } catch (e) {
        this.$toast.error("Error al guardar pantalla");
      } finally {
        this.loading = false;
      }
    },
    async saveFeature() {
      if (!this.new_feature.view_id || !this.new_feature.access_type_id || !this.new_feature.code || !this.new_feature.description) {
        return this.warningNotification("Todos los campos son obligatorios");
      }
      this.loading = true;
      try {
        const res = await this.api_Post("auth/save_system_feature_new", this.new_feature);
        if (res.ok) {
          this.$toast.success("Acceso guardado");
          this.new_feature = { view_id: null, access_type_id: null, code: null, description: null };
          await this.initialize();
        }
      } catch (e) {
        this.$toast.error("Error al guardar acceso");
      } finally {
        this.loading = false;
      }
    },
    add_profile() {
      this.page = 2;
    },
    edit_profile(profile) {
      this.new_profile.profile_id = profile.id;
      this.new_profile.module_id = profile.module_id;
      this.new_profile.description = profile.description;
      this.new_profile.code = profile.code;
      for (let key in this.features) {
        this.features[key].include = profile.roles.includes(this.features[key].feature_id);
      }
      this.page = 2;
    },
    clear_features() {
      for (let key in this.features) {
        this.features[key].include = false;
      }
    },
    async save_system_profile() {
      if (this.new_profile.module_id == null || !this.new_profile.description || !this.new_profile.code) {
        return this.warningNotification("Complete todos los campos del perfil");
      }
      try {
        let features = this.features.filter(e => e.include).map(e => e.feature_id);
        if (features.length == 0) return this.warningNotification("Seleccione al menos un acceso");
        this.loading = true;
        const result = await this.api_Post("auth/save_system_profile", {
          ...this.new_profile,
          perfil_code: this.new_profile.code,
          features_ids: features,
        });
        if (result.ok) {
          this.$toast.success("Perfil guardado");
          this.clear();
          await this.initialize();
        }
      } catch (error) {
        this.$toast.error("Error: " + error.message);
      } finally {
        this.loading = false;
      }
    },
    clear() {
      this.clear_features();
      this.new_profile = { module_id: null, profile_id: null, code: null, description: null, set_update: false };
      this.page = 1;
    },
    info_set_update() {
      if (this.new_profile.set_update) {
        this.$toast.info("Esto actualizará los accesos de todos los usuarios vinculados");
      }
    },
    getAccessTypeColor(type) {
      if (!type) return "grey";
      const colors = { ESCRITURA: "success", LECTURA: "info", ELIMINACION: "error", ESPECIAL: "warning" };
      return colors[type.toUpperCase()] || "primary";
    },
    getAccessTypeIcon(type) {
      if (!type) return "help_outline";
      const icons = { ESCRITURA: "edit", LECTURA: "visibility", ELIMINACION: "delete", ESPECIAL: "star" };
      return icons[type.toUpperCase()] || "lock";
    },
    featureRowClass(item) {
      return this.selectedFeature && this.selectedFeature.feature_id === item.feature_id
        ? "selected-feature-row"
        : "";
    },
    async selectFeatureRow(item) {
      // Al seleccionar un acceso, cargar los usuarios que lo tienen
      this.selectedFeature = item;
      this.featureUsers = [];
      this.featureUserSearch = "";
      this.featurePanelTab = 0;
      this.loadingFeatureUsers = true;
      try {
        const res = await this.api_Get("auth/users_by_feature/" + item.feature_id);
        this.featureUsers = await res.json();
      } catch (e) {
        this.$toast.error("No se pudieron cargar los usuarios de este acceso");
      } finally {
        this.loadingFeatureUsers = false;
      }
    },
    toggleAllGroups() {
      const t = this.$refs.permTable;
      if (!t || !t.openCache) return;
      this.allGroupsCollapsed = !this.allGroupsCollapsed;
      const open = !this.allGroupsCollapsed; // true = abierto
      // Asegura que todas las pantallas (grupos) estén en el cache y aplica el estado
      const groups = [
        ...new Set((this.features || []).map((f) => f.view_description)),
      ];
      groups.forEach((g) => this.$set(t.openCache, g, open));
      // Por si quedaron grupos de un módulo anterior en el cache
      Object.keys(t.openCache).forEach((k) => this.$set(t.openCache, k, open));
    },
  },
};
</script>

<style scoped>
.transparent-stepper {
  background-color: transparent !important;
  box-shadow: none !important;
}

/* Filas de accesos clicables + resaltado de la seleccionada */
.clickable-rows :deep(tbody tr) {
  cursor: pointer;
}
.clickable-rows :deep(tr.selected-feature-row) {
  background-color: var(--v-primary-lighten5, #e3f2fd) !important;
}

.modern-card {
  border-radius: 16px !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(10px);
}

.page-header {
  border-left: 5px solid #1976d2;
  padding-left: 16px;
}

.search-field {
  max-width: 300px;
}

.module-filter {
  max-width: 250px;
}

.search-field-small {
  max-width: 200px;
}

.modern-table :deep(.v-data-table-header) {
  background-color: #f8f9fa !important;
}

.modern-table :deep(th) {
  text-transform: uppercase;
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  color: #5e6e82 !important;
  letter-spacing: 0.05em;
}

.group-header-row {
  background-color: #f1f5f9 !important;
}

.feature-table :deep(tr:hover) {
  background-color: #f8fafc !important;
}

.sticky-card {
  position: sticky;
  top: 24px;
}

.search-field-dialog {
  max-width: 400px;
}

.sidebar-nav :deep(.v-list-item--active) {
  background: #eef2ff !important;
}

.sidebar-nav :deep(.v-list-item--active .v-icon) {
  color: #1976d2 !important;
}

.modern-table :deep(.v-data-table__wrapper) {
  border-radius: 8px;
}
</style>